import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { Filter } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { UIProps } from '../../theme/mixins';
import SC from '../../common/UI/SC';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';

interface StatBarProps {
  values: number[];
  max?: number;
  colors: string[];
  children?: ReactNode;
  hideContent?: boolean;
}

export const StatBar = ({ values, max, colors, hideContent = false, children }: StatBarProps) => {
  const total = typeof max !== 'undefined' ? max : values.reduce((sum, value) => sum + value, 0);

  return (
    <StatBarContainer hideContent={!children || hideContent} fadeOnHover={!!children}>
      <UI.Div>
        {values.map((value, index) => value > 0 && (
          <UI.Div
            style={{ background: colors[index], width: `${(value / total) * 100}%` }}
            key={index}
          />
        ))}
      </UI.Div>
      <UI.Div>
        {children}
      </UI.Div>
    </StatBarContainer>
  );
};

const StatBarContainer = styled(UI.Div)<{ hideContent?: boolean; fadeOnHover?: boolean; }>`
  ${({ hideContent = false, fadeOnHover = false, theme }) => css`
    padding: ${theme.gutter / 6}px ${theme.gutter / 6}px;
    position: relative;
    height: 28px;
    line-height: 20px; // 28 - 4 - 4

    > div:first-child {
      position: absolute;
      top: 2px;
      left: 0;
      bottom: 2px;
      right: 0;
      height: 24px;
      width: 100%;
      z-index: 1;
      display: flex;

      ${!hideContent && css`
        opacity: 0.12;
      `}

      > div {
        height: 24px;
        transition: width 0.15s ease-in-out;
        box-shadow: -1px 0 0 white;
        min-width: 2px;
      }
    }

    > div:last-child {
      position: absolute;
      left: ${theme.gutter / 6}px;
      top: ${theme.gutter / 6}px;
      right:  ${theme.gutter / 6}px;
      z-index: 2;
      transition: all 0.15s ease-in-out;

      ${hideContent && css`
        opacity: 0;
      `}
    }

    ${fadeOnHover && css`
      &:hover {
        > div:first-child {
          opacity: 0.12;
        }

        > div:last-child {
          opacity: 1;
        }
      }
    `}
  `}
`;

export const StickyStatsListCard = ({ sc, ...props }: any) => (
  <UI.StickyCard sc={{ small: [true, false], pt: 2, pb: 2, ...sc }} {...props} />
);

export const StickyStatsListHeader = ({ sc, ...props }: any) => (
  <UI.StickyCardHeader sc={{ pt: [1, 2], pb: [2, 3], ...sc }} {...props} />
);

export const StatsListContainer = styled(UI.Div)`
  padding: 0;
  border-top: 0;
`;

export const StatsListRow = styled(UI.Div)`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.gray[100]};
    display: grid;
    grid-gap: ${theme.gutter / 4}px;
    grid-template-columns: 2fr 1fr;
    padding: ${theme.gutter / 3}px ${theme.gutter * 0.75}px;

    @media ${theme.devices.md} {
      grid-template-columns: 4fr 1fr 2fr 2fr;
      padding: ${theme.gutter / 3}px ${theme.gutter}px;
    }
  `}
`;

export const StatsListFooter = styled(UI.Div)`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.gray[100]};
    padding: ${theme.gutter * 0.5}px ${theme.gutter * 0.75}px ${theme.gutter * 0.25}px ${theme.gutter * 0.75}px;

    @media ${theme.devices.md} {
      padding: ${theme.gutter * 0.5}px ${theme.gutter}px;
    }
  `}
`;

export const StatsListHeader = styled(UI.Div)`
  ${({ theme }) => css`
    display: grid;
    grid-gap: ${theme.gutter / 4}px;
    align-items: center;

    @media ${theme.devices.sm} {
      grid-template-columns: 2fr 1fr;
    }

    @media ${theme.devices.md} {
      grid-template-columns: 4fr 1fr 2fr 2fr;
    }
  `}
`;

export const StatsListLoader = () => (
  <StatsListRow sc={{ background: 'gray.50', pl: 2 }}>
    <UI.Div>
      <UI.Loader />
    </UI.Div>
  </StatsListRow>
);

interface ProgressBarProps {
  count: number;
  capacity: number;
  secondary?: number | null;
  tooltip?: ReactNode;
}

export const ProgressBar = ({ count, capacity, secondary = 0, tooltip }: ProgressBarProps) => {
  const [progress, setProgress] = useState(0);
  const [extra, setExtra] = useState(0);

  const calculatedCapacity = Math.max(capacity, count + secondary);
  const calculatedProgress = Math.min(100, (count / calculatedCapacity) * 100);
  const calculatedExtra = Math.min(100, (secondary / calculatedCapacity) * 100);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setProgress(calculatedProgress);
      setExtra(calculatedExtra);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [calculatedProgress, calculatedExtra]);

  return (
    <UI.TooltipContainer style={{ width: 60, height: 22, paddingTop: 7, paddingBottom: 7 }}>
      {tooltip && (
        <UI.Tooltip sc={{ alignH: 'center', alignV: 'top', small: true }}>
          {tooltip}
        </UI.Tooltip>
      )}
      <UI.Div
        sc={{ background: 'gray.200' }}
        style={{
          height: '100%', width: '100%', borderRadius: '4px', overflow: 'hidden', display: 'flex',
        }}
      >
        <UI.Div
          sc={{ background: 'success' }}
          style={{
            height: '100%',
            width: `${progress}%`,
            transition: 'all 0.25s ease-in-out',
          }}
        />
        <UI.Div
          sc={{ background: 'info' }}
          style={{
            height: '100%',
            width: `${extra}%`,
            transition: 'all 0.25s ease-in-out',
          }}
        />
      </UI.Div>
    </UI.TooltipContainer>
  );
};

interface FilterIndicatorProps extends SC<UIProps> {
  alignV?: 'top' | 'bottom';
  style?: CSSProperties;
}

export const FilterIndicator = ({ alignV = 'bottom', sc, style }: FilterIndicatorProps) => {
  const { t } = useTranslation();
  const [urlState] = useDashboardUrlState();

  if (urlState.tickets.length === 0) {
    return null;
  }

  return (
    <UI.TooltipContainer sc={{ my: -0.5, ...sc }} style={style}>
      <UI.Tooltip style={{ left: -4, width: 200, whiteSpace: 'normal' }} sc={{ alignV }}>
        {t('dashboard_filters_active_tooltip')}
      </UI.Tooltip>
      <UI.Badge sc={{ brand: 'gray.100' }} style={{ width: 22, height: 22 }}>
        <UI.Small>
          <UI.Icon sc={{ color: 'gray.500' }}>
            <Filter />
          </UI.Icon>
        </UI.Small>
      </UI.Badge>
    </UI.TooltipContainer>
  );
};

export default undefined;
